// @rpg-unit/settings: the settings feature (tier 4; composition framework, decision 0077). Cut from spike 002's
// `src/ui/settings.ts`, `src/ui/index.ts` (the hold-or-toggle mirror and the saved value) and `src/ui/screens.ts` (the
// settings screen) on 2026-10-20. Over @rpg/ui's settings model and panel it gives a game:
//
//   - `settingDefs(rows)`: setting rows (@rpg-unit/standard-settings' and the game's own, same shape) as the model's
//     definitions;
//   - `mountSettings(rows, deps)`: the model, applied live now and on every change: text size (the UI's text scale),
//     contrast, reduce motion (following the OS while set to "system", decision 0063 in spike 002) and the graphics
//     tier ("auto" takes the device's);
//   - `bindHoldModes(settings, rows, modes)`: the `controls.<action>` rows kept in step with the input's hold-or-toggle
//     modes both ways (the input's model holds the mode; a setting change sets it; a change elsewhere is mirrored);
//   - `persistSettings(o)`: the values (and the input's binding overrides) loaded once from a store, then written
//     (debounced) on every change, and reloaded when a sync brings them in from another device;
//   - `settingsScreen(o)`: the screen (the panel by section, then links to other screens and "reset to defaults").
//
// The seams: rows come in as data (any feature or the game adds its own); other features read values through the
// `Settings` model it returns (@rpg/ui's type) and `subscribe` to changes; the store and the input's modes are ports.
// It imports no other feature.
import { Button, h, Menu, Screen, SettingsPanel, createSettings, type ScreenContext, type ScreenRender, type SettingDef, type Settings, type Ui } from "@rpg/ui";
import { STANDARD_SETTING_GROUPS, holdSettingId, type SettingRow } from "@rpg-unit/standard-settings";

/** This unit's version (the manifest's and package.json's). */
export const SETTINGS_VERSION = "0.1.0" as const;

export type { SettingRow } from "@rpg-unit/standard-settings";

/** Setting rows as the UI model's definitions (labels are string ids). */
export function settingDefs(rows: readonly SettingRow[]): SettingDef[] {
  return rows.map((r) => {
    const base = { id: r.id, group: r.group, label: r.label.id };
    if (r.kind === "range") return { ...base, kind: "range", default: r.default as number, min: r.min!, max: r.max!, step: r.step } as SettingDef;
    if (r.kind === "toggle") return { ...base, kind: "toggle", default: r.default as boolean };
    return { ...base, kind: "choice", default: r.default as string, options: (r.options ?? []).map((o) => ({ value: o.value, label: o.label.id })) };
  });
}

/** What applying the settings live needs from the game. */
export interface ApplyDeps {
  /** The UI root the text scale, contrast and reduce motion go to. */
  ui: Pick<Ui, "setTextScale" | "setContrast" | "setReduceMotion">;
  /** The text scale for a text size choice (`small`, `medium`, `large`): the game's numbers. */
  textScale: (size: string) => number;
  /** Set the graphics tier the game runs at ("low", "high"; "auto" passes the device's tier here). */
  quality: (tier: string) => void;
  /** The device's own tier (the boot's pick). */
  deviceTier: () => string;
  /** The OS's reduced-motion query (default: `matchMedia("(prefers-reduced-motion: reduce)")` where there is one). */
  osReduceMotion?: { matches: boolean; addEventListener(type: "change", fn: () => void): void } | null;
  /** Where `data-reduce-motion` goes (default: the document's root, where there is a document). */
  root?: { toggleAttribute(name: string, on: boolean): unknown } | null;
}

/** The values in force that aren't just the stored ones (reduce motion resolved against the OS, the tier picked). */
export interface Applied {
  textScale: number;
  highContrast: boolean;
  reduceMotion: boolean;
  tier: string;
}

/** The setting ids applying reads (the standard rows'). */
export const APPLIED_SETTINGS = { textSize: "accessibility.textSize", reduceMotion: "accessibility.reduceMotion", quality: "display.quality", contrast: "accessibility.contrast" } as const;

/** Planted defects, so a game's checks can prove they fail: `ignoreOs` resolves "system" reduce motion as off whatever
 *  the OS says; `writeBackOnLoad` writes loaded values straight back (a sync from another device would echo forever). */
export const SETTINGS_FAULTS = { ignoreOs: false, writeBackOnLoad: false };

/** Build the model over the rows, apply it now and on every change. */
export function mountSettings(rows: readonly SettingRow[], d: ApplyDeps): { settings: Settings; applied(): Applied } {
  const settings = createSettings(settingDefs(rows));
  const osReduce = d.osReduceMotion !== undefined ? d.osReduceMotion : typeof matchMedia === "function" ? matchMedia("(prefers-reduced-motion: reduce)") : null;
  const root = d.root !== undefined ? d.root : typeof document !== "undefined" ? document.documentElement : null;
  let applied: Applied = { textScale: 1, highContrast: false, reduceMotion: false, tier: d.deviceTier() };
  const apply = (): void => {
    const size = settings.get<string>(APPLIED_SETTINGS.textSize);
    const motion = settings.get<string>(APPLIED_SETTINGS.reduceMotion);
    const quality = settings.get<string>(APPLIED_SETTINGS.quality);
    applied = {
      textScale: d.textScale(size),
      highContrast: settings.get<string>(APPLIED_SETTINGS.contrast) === "high",
      reduceMotion: motion === "on" || (motion === "system" && !SETTINGS_FAULTS.ignoreOs && !!osReduce?.matches),
      tier: quality === "auto" ? d.deviceTier() : quality,
    };
    d.ui.setTextScale(applied.textScale);
    d.ui.setContrast(applied.highContrast);
    d.ui.setReduceMotion(applied.reduceMotion);
    d.quality(applied.tier);
    root?.toggleAttribute("data-reduce-motion", applied.reduceMotion);
  };
  settings.subscribe(apply);
  osReduce?.addEventListener("change", apply);
  apply();
  return { settings, applied: () => ({ ...applied }) };
}

/** The input's hold-or-toggle modes, as the feature reads and writes them (@rpg/runtime's `Rebinding` fits). */
export interface HoldModes {
  holdMode(action: string): string;
  setHoldMode(action: string, mode: "hold" | "toggle"): void;
  onChange(fn: () => void): unknown;
}

/**
 * Keep the `controls.<action>` rows and the input's modes in step: a setting change sets the input's mode; the input's
 * mode changing elsewhere (its own screen, a load, a sync) is mirrored into the setting. Mirrors once now. Returns
 * `sync`, the mirror (input → settings), to run after the input's modes are loaded.
 */
export function bindHoldModes(settings: Settings, rows: readonly SettingRow[], modes: HoldModes): { sync(): void } {
  const holdRows = rows.filter((row) => row.id.startsWith(holdSettingId("")));
  let mirroring = false;
  const toInput = (): void => {
    if (mirroring) return;
    for (const row of holdRows) {
      const action = row.id.slice(holdSettingId("").length);
      const mode = settings.get<string>(row.id);
      if ((mode === "hold" || mode === "toggle") && modes.holdMode(action) !== mode) modes.setHoldMode(action, mode);
    }
  };
  const toSettings = (): void => {
    mirroring = true;
    try {
      for (const row of holdRows) {
        const mode = modes.holdMode(row.id.slice(holdSettingId("").length));
        if (settings.get<string>(row.id) !== mode) settings.set(row.id, mode);
      }
    } finally {
      mirroring = false;
    }
  };
  settings.subscribe(toInput);
  modes.onChange(toSettings);
  toSettings();
  return { sync: toSettings };
}

/** Where the settings are kept: one saved value (`{ values, bindings }`), and syncs that bring it in. */
export interface SettingsStore {
  load(): Promise<unknown>;
  save(value: { values: Record<string, unknown>; bindings?: unknown }): Promise<void>;
  /** Hear a sync bring the value in from another device (optional). */
  onPulled?(fn: () => void): void;
}

/** The input's binding overrides saved beside the values (optional; the rebinding model fits). */
export interface SavedBindings {
  overrides(): unknown;
  load(saved: unknown): void;
  onChange(fn: () => void): unknown;
}

/** What `persistSettings` needs. */
export interface PersistOptions {
  settings: Settings;
  store: SettingsStore;
  /** Wait this long after the last change before writing, ms. */
  delayMs: number;
  bindings?: SavedBindings;
  /** After saved values are applied (and before writes resume): e.g. the hold modes' mirror. */
  afterLoad?: () => void;
  /** After every load, whatever it found (e.g. redraw the UI). */
  settled?: () => void;
  /** Where problems go (default console.warn). */
  warn?: (message: string, error?: unknown) => void;
}

/**
 * Load the saved settings once, then write them (debounced) on every change of the values or the bindings; reload
 * when the store says a sync brought them in. Applying loaded values is not a change to write back. `ready` resolves
 * once the first load settled.
 */
export function persistSettings(o: PersistOptions): { ready: Promise<void>; reload(): Promise<void> } {
  const warn = o.warn ?? ((m: string, e?: unknown) => console.warn(m, ...(e === undefined ? [] : [e])));
  let loaded = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const persist = (): void => {
    if (!loaded) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void o.store.save({ values: o.settings.values(), ...(o.bindings ? { bindings: o.bindings.overrides() } : {}) }).catch((e) => warn("[ui] settings not saved", e));
    }, o.delayMs);
  };
  const reload = (): Promise<void> => {
    loaded = SETTINGS_FAULTS.writeBackOnLoad; // applying saved values is not a change to write back
    return o.store
      .load()
      .then((saved) => {
        const v = saved as { values?: Record<string, unknown>; bindings?: unknown } | null;
        const issues = v?.values ? o.settings.load(v.values) : [];
        if (issues.length) warn(`[ui] saved settings skipped: ${issues.join("; ")}`);
        if (v?.bindings && o.bindings) o.bindings.load(v.bindings);
        o.afterLoad?.();
      })
      .catch((e) => warn("[ui] settings not loaded", e))
      .finally(() => {
        loaded = true;
        o.settled?.();
      });
  };
  const ready = reload();
  o.settings.subscribe(persist);
  o.bindings?.onChange(persist);
  o.store.onPulled?.(() => void reload());
  return { ready, reload };
}

/** A range's value as a player reads it: a share as a percentage (ranges up to 1), else a multiplier ("1.5×"); in the
 *  locale's digits, so the raw-string scan (which lets numbers, % and × through) stays quiet. */
export function rangeText(def: SettingDef & { kind: "range" }, v: number, locale?: string): string {
  return def.max <= 1 ? new Intl.NumberFormat(locale, { style: "percent" }).format(v) : `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(v)}×`;
}

/** A link from the settings screen to another (`controls`, the account). */
export interface SettingsLink {
  label: string;
  action: string;
  screen: string;
}

/** What the settings screen shows. */
export interface SettingsScreenOptions {
  /** The model (read at render time, so it may be set after the screen is registered). */
  settings: () => Settings;
  /** Sections in order (default: the standard groups). */
  groups?: readonly { id: string; label: string }[];
  /** Buttons under the panel, before "reset to defaults". */
  links?: readonly SettingsLink[];
  /** String ids: the title, a toggle's on and off, the reset button. */
  labels?: { title?: string; on?: string; off?: string; reset?: string };
  /** A range's value as text (default {@link rangeText}). */
  format?: (def: SettingDef & { kind: "range" }, v: number, ctx: ScreenContext) => string;
}

/** The settings screen: the panel by section, the links, then "reset to defaults" (`data-action="reset-settings"`). */
export function settingsScreen(o: SettingsScreenOptions): ScreenRender {
  const l = { title: "ui.settings", on: "option.on", off: "option.off", reset: "ui.resetDefaults", ...o.labels };
  const groups = o.groups ?? STANDARD_SETTING_GROUPS;
  // the formatters are made once, as the locale's (a message that is only a placeholder would blind the raw-string scan)
  const pct = new Intl.NumberFormat(undefined, { style: "percent" });
  const num = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
  const format = o.format ?? ((def: SettingDef & { kind: "range" }, v: number) => (def.max <= 1 ? pct.format(v) : `${num.format(v)}×`));
  return (ctx) => {
    const settings = o.settings();
    return h(
      Screen,
      { ctx, title: ctx.t(l.title), dim: true },
      h(SettingsPanel, { ctx, settings, groups, on: l.on, off: l.off, format: (def, v) => format(def, v, ctx) }),
      h(
        Menu,
        null,
        ...(o.links ?? []).map((x) => h(Button, { label: ctx.t(x.label), action: x.action, onPress: () => ctx.ui.push(x.screen) })),
        h(Button, {
          label: ctx.t(l.reset),
          action: "reset-settings",
          onPress: () => {
            settings.reset();
            ctx.ui.refresh();
          },
        }),
      ),
    );
  };
}
